import React from 'react';
import { useSelector } from 'react-redux';
import { CircleCheckIcon, ClockIcon, PhoneCallIcon, SuccessCheckRingIcon } from '../../assets/icons';
import MailIcon from '../../assets/icons/MailIcon';
import ShieldCheckIcon from '../../assets/icons/ShieldCheckIcon';

function UnderReview() {
  const user = useSelector((state) => state.user?.user);
  const firstName = user?.firstName?.trim();
  const email = user?.email || user?.userDetails?.email;

  return (
    <div className='bg-[#EBF4FB] font-sans text-[#0B1929]'>
      <section className='relative overflow-hidden bg-[linear-gradient(135deg,#060D1A_0%,#0F2540_55%,#163659_100%)] px-6 pb-12 pt-10 text-center after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-8 after:rounded-t-[32px] after:bg-[#EBF4FB]'>
        <div className='relative z-[1] mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full border-2 border-white/20 bg-white/10 text-white/80'>
          <SuccessCheckRingIcon className='h-11 w-11' />
        </div>
        <h1 className="relative z-[1] font-['Georgia','Times_New_Roman',serif] text-[28px] font-normal text-white">
          Application submitted{firstName ? `, ${firstName}` : ''}.
        </h1>
        <p className='relative z-[1] mt-2 text-[14.5px] text-white/60'>We&apos;re reviewing everything now.</p>
      </section>

      <main className='flex flex-col items-center px-6 pb-8 pt-8'>
        <div className='mb-4 w-full max-w-[480px] rounded-2xl border border-[#0B1929]/[0.08] bg-white px-7 py-6 shadow-[0_2px_16px_rgba(11,25,41,0.06)]'>
          <div className='mb-4 text-[11px] font-bold uppercase tracking-[0.08em] text-[#7A9AB0]'>Application status</div>
          <div className='flex items-center gap-3.5 border-b border-[#EEF3F8] py-3'>
            <div className='flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-green-100 text-green-600'>
              <CircleCheckIcon className='h-[18px] w-[18px] stroke-[2.5]' />
            </div>
            <div className='flex-1 text-sm font-semibold text-[#0B1929]'>Account created</div>
            <span className='text-xs font-semibold text-green-600'>Complete</span>
          </div>
          <div className='flex items-center gap-3.5 border-b border-[#EEF3F8] py-3'>
            <div className='flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-green-100 text-green-600'>
              <ShieldCheckIcon className='h-[18px] w-[18px] stroke-[2.5]' />
            </div>
            <div className='flex-1 text-sm font-semibold text-[#0B1929]'>Identity verified</div>
            <span className='text-xs font-semibold text-green-600'>Complete</span>
          </div>
          <div className='flex items-center gap-3.5 border-b border-[#EEF3F8] py-3'>
            <div className='flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-yellow-100 text-amber-800'>
              <ClockIcon className='h-[18px] w-[18px] stroke-2' />
            </div>
            <div className='flex-1 text-sm font-semibold text-[#0B1929]'>
              Compliance review
              <div className='mt-px text-xs font-normal text-[#7A9AB0]'>1-2 business days</div>
            </div>
            <span className='text-xs font-semibold text-amber-800'>In review</span>
          </div>
          <div className='flex items-center gap-3.5 py-3'>
            <div className='flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-[#EBF4FB] text-[#2E6BAD]'>
              <PhoneCallIcon className='h-[18px] w-[18px] stroke-2' />
            </div>
            <div className='flex-1 text-sm font-semibold text-[#0B1929]'>
              Account activation
              <div className='mt-px text-xs font-normal text-[#7A9AB0]'>
                {email ? `We'll email you at ${email} once approved` : "We'll email you once approved"}
              </div>
            </div>
            <span className='text-xs font-semibold text-[#7A9AB0]'>Pending</span>
          </div>
        </div>

        <div className='mb-4 w-full max-w-[480px] rounded-2xl border border-[#0B1929]/[0.08] bg-white px-7 py-6 shadow-[0_2px_16px_rgba(11,25,41,0.06)]'>
          <div className='mb-4 text-[11px] font-bold uppercase tracking-[0.08em] text-[#7A9AB0]'>While you wait</div>
          <p className='mb-4 text-sm leading-6 text-[#3D5A73]'>
            Our compliance team reviews all applications manually to meet EU regulatory requirements. You&apos;ll receive a confirmation email once
            your account is approved.
          </p>
          <div className='flex items-center gap-2 text-[13px] text-[#7A9AB0]'>
            <MailIcon className='h-4 w-4 shrink-0 text-[#2E6BAD]' />
            <span>
              Questions? Contact us at{' '}
              <a href='mailto:hello@hansepay.com' className='font-medium text-[#2E6BAD] hover:underline'>
                hello@hansepay.com
              </a>
            </span>
          </div>
        </div>

        <div className='flex w-full max-w-[480px] flex-col justify-center gap-3 sm:flex-row'>
          <a
            href='https://www.hansepay.de/'
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex min-h-[48px] min-w-[200px] items-center justify-center rounded-full bg-[#0B1929] px-7 text-sm font-semibold text-white transition hover:bg-[#0F2540]'
          >
            Back to HansePay
          </a>
          <a
            href='https://www.hansepay.de/booking.html'
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex min-h-[48px] min-w-[200px] items-center justify-center rounded-full border-[1.5px] border-[#0B1929]/15 bg-transparent px-7 text-sm font-semibold text-[#3D5A73] transition hover:border-[#8DBDE6] hover:bg-white'
          >
            Book a discovery call
          </a>
        </div>
      </main>
    </div>
  );
}

export default UnderReview;
